<script setup>
import {ref, onMounted, onBeforeUnmount} from 'vue'
import {Swiper, SwiperSlide} from 'swiper/vue'
import {Pagination, Autoplay, A11y} from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'

import IconAngleLeft from '@js/components/Icons/IconAngleLeft.vue'
import {Head, Link} from '@inertiajs/vue3'

const modules = [Pagination, Autoplay, A11y]
const swiper = ref(null)
const isClient = ref(false)
const rootEl = ref(null)
const autoplay = ref(false)
const hideSSRHero = ref(false) // скроем SSR-герой после инициализации Swiper

const setSwiper = s => {
    swiper.value = s
}
const onSwiperInit = s => {
    setSwiper(s)
    // даём кадр Swiper отрисоваться и скрываем SSR-герой
    requestAnimationFrame(() => {
        hideSSRHero.value = true
    })
}
const slideNext = () => swiper.value?.slideNext()
const slidePrev = () => swiper.value?.slidePrev()

// только 2 файла на слайд: desktop и mobile
import SL1 from '@img/home_slider/1.webp'
import SL1_M from '@img/home_slider/1_m.webp'
import SL2 from '@img/home_slider/2.webp'
import SL2_M from '@img/home_slider/2_m.webp'
import SL3 from '@img/home_slider/3.webp'
import SL3_M from '@img/home_slider/3_m.webp'
import SL4 from '@img/home_slider/4.webp'
import SL4_M from '@img/home_slider/4_m.webp'
import SL5 from '@img/home_slider/5.webp'
import SL5_M from '@img/home_slider/5_m.webp'

let io
onMounted(() => {
    isClient.value = true
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    autoplay.value = reduce ? false : {delay: 5000, disableOnInteraction: true, pauseOnMouseEnter: true}

    io = new IntersectionObserver((entries) => {
        const entry = entries[0]
        if (!swiper.value?.autoplay) return
        entry.isIntersecting ? swiper.value.autoplay.start() : swiper.value.autoplay.stop()
    }, {threshold: 0.2})
    if (rootEl.value) io.observe(rootEl.value)
})
onBeforeUnmount(() => io?.disconnect())

const buildCatalogUrl = (typeSlug, categories = [], subCategories = []) => {
    const base = route('catalog', {type_slug: typeSlug})
    const params = new URLSearchParams()

    if (categories.length) {
        params.set('categories', JSON.stringify(categories))
    }

    if (subCategories.length) {
        params.set('sub_categories', JSON.stringify(subCategories))
    }

    const qs = params.toString()
    return qs ? `${base}?${qs}` : base
}
</script>

<template>
    <!-- 1) Preload LCP (desktop + mobile) -->
    <Head>
        <link rel="preload" as="image" :href="SL1" fetchpriority="high" imagesizes="100vw"/>
        <link rel="preload" as="image" :href="SL1_M" fetchpriority="high" media="(max-width: 480px)"
              imagesizes="100vw"/>
    </Head>

    <div class="group relative" ref="rootEl">
        <!-- 2) SSR-герой (тот же кадр, что и первый слайд), виден до инициализации Swiper -->
        <Link v-show="!hideSSRHero"
              :href="buildCatalogUrl('zhbi')"
              class="relative block w-full h-[200px] md:h-[400px] rounded-3xl overflow-hidden"
              aria-label="Открыть раздел">
            <picture class="absolute inset-0 block" aria-hidden="true">
                <source media="(max-width: 480px)" :srcset="SL1_M" type="image/webp"/>
                <source :srcset="SL1" type="image/webp"/>
                <!-- width/height задают аспект; подставь реальные размеры 1.webp -->
                <img :src="SL1" alt="" width="1368" height="400"
                     class="w-full h-full object-cover"
                     loading="eager" fetchpriority="high"/>
            </picture>
        </Link>

        <!-- Стрелка "назад" -->
        <button v-if="swiper" @click.prevent="slidePrev" aria-label="Назад"
                class="max-md:hidden opacity-0 group-hover:opacity-100 absolute top-1/2 -translate-y-1/2 left-2 z-10 bg-baseColor md:hover:bg-baseColor/70 text-white rounded-full w-10 h-10 flex items-center justify-center transition backdrop-blur">
            <IconAngleLeft class="w-5 h-5 text-black"/>
        </button>

        <div class="relative w-full">
            <Swiper
                v-if="isClient"
                :slides-per-view="1"
                :space-between="16"
                :loop="true"
                :modules="modules"
                :pagination="{ clickable: true }"
                :autoplay="autoplay"
                :preload-images="false"
                class="hero-slide w-full h-[200px] md:h-[400px] rounded-lg md:rounded-3xl overflow-hidden pagination-base long_bullet main-slider bg-transparent"
                @swiper="onSwiperInit"
                :a11y="{
          enabled: true,
          containerMessage: 'Слайдер промо-баннеров',
          containerRoleDescriptionMessage: 'Карусель',
          itemRoleDescriptionMessage: 'Слайд',
          slideLabelMessage: 'Слайд {{index}} из {{slidesLength}}',
          prevSlideMessage: 'Предыдущий слайд',
          nextSlideMessage: 'Следующий слайд',
          paginationBulletMessage: 'Перейти к слайду {{index}}'
        }"
                aria-label="Промо-баннеры металлопроката"
            >
                <!-- SLIDE 1 (LCP, без lazy/async) -->
                <SwiperSlide>
                    <Link :href="buildCatalogUrl('zhbi', ['dorozhno-transportnoe_stroitelstvo'], ['dorozhnye_plity'])"
                          class="relative block w-full h-full"
                          aria-label="Открыть раздел">
                        <picture class="absolute inset-0 block" aria-hidden="true">
                            <source media="(max-width: 480px)" :srcset="SL1_M" type="image/webp"/>
                            <source :srcset="SL1" type="image/webp"/>
                            <img :src="SL1" alt="" width="1368" height="400"
                                 class="w-full h-full object-cover"
                                 loading="eager" fetchpriority="high"/>
                        </picture>
                    </Link>
                </SwiperSlide>
                <SwiperSlide>
                    <Link :href="buildCatalogUrl('beton')"
                           class="relative block w-full h-full"
                          aria-label="Открыть раздел">
                        <picture class="absolute inset-0 block" aria-hidden="true">
                            <source media="(max-width: 480px)" :srcset="SL2_M" type="image/webp"/>
                            <source :srcset="SL2" type="image/webp"/>
                            <img :src="SL2" alt="" width="1368" height="400"
                                 class="w-full h-full object-cover"
                                 loading="eager" fetchpriority="high"/>
                        </picture>
                    </Link>
                </SwiperSlide>
                <SwiperSlide>
                    <Link :href="route('comingSoon')"
                          class="relative block w-full h-full"
                          aria-label="Открыть раздел">
                        <picture class="absolute inset-0 block" aria-hidden="true">
                            <source media="(max-width: 480px)" :srcset="SL3_M" type="image/webp"/>
                            <source :srcset="SL3" type="image/webp"/>
                            <img :src="SL3" alt="" width="1368" height="400"
                                 class="w-full h-full object-cover"
                                 loading="eager" fetchpriority="high"/>
                        </picture>
                    </Link>
                </SwiperSlide>
                <SwiperSlide>
                    <Link :href="route('comingSoon')"
                          class="relative block w-full h-full"
                          aria-label="Открыть раздел">
                        <picture class="absolute inset-0 block" aria-hidden="true">
                            <source media="(max-width: 480px)" :srcset="SL4_M" type="image/webp"/>
                            <source :srcset="SL4" type="image/webp"/>
                            <img :src="SL4" alt="" width="1368" height="400"
                                 class="w-full h-full object-cover"
                                 loading="eager" fetchpriority="high"/>
                        </picture>
                    </Link>
                </SwiperSlide>
                <SwiperSlide>
                    <Link :href="route('comingSoon')"
                          class="relative block w-full h-full"
                          aria-label="Открыть раздел">
                        <picture class="absolute inset-0 block" aria-hidden="true">
                            <source media="(max-width: 480px)" :srcset="SL5_M" type="image/webp"/>
                            <source :srcset="SL5" type="image/webp"/>
                            <img :src="SL5" alt="" width="1368" height="400"
                                 class="w-full h-full object-cover"
                                 loading="eager" fetchpriority="high"/>
                        </picture>
                    </Link>
                </SwiperSlide>
            </Swiper>
        </div>

        <!-- Стрелка "вперёд" -->
        <button v-if="swiper" @click.prevent="slideNext" aria-label="Вперед"
                class="max-md:hidden opacity-0 group-hover:opacity-100 absolute top-1/2 -translate-y-1/2 right-2 z-10 bg-baseColor md:hover:bg-baseColor/70 text-white rounded-full w-10 h-10 flex items-center justify-center transition backdrop-blur">
            <IconAngleLeft class="w-5 h-5 text-black rotate-180"/>
        </button>
    </div>
</template>

<style scoped lang="scss">
::v-deep(.swiper-pagination-bullet) {
    background: #000 !important;
    opacity: .6;
    transition: opacity .3s ease
}

::v-deep(.swiper-pagination-bullet-active) {
    background: #000 !important;
    opacity: 1
}

@media (prefers-reduced-motion: reduce) {
    :deep(.swiper-wrapper) {
        transition-duration: 0ms !important
    }
}

/* Мобилки: делаем 2:1 по ширине */
@media (max-width: 768px) {
    .hero-slide {
        aspect-ratio: 2 / 1; /* ширина / высота */
        height: auto; /* чтобы работал aspect-ratio */
    }
    .hero-slide > img {
        width: 100%;
        height: 100%;
        object-fit: cover; /* без искажений, заполняем кадр */
        display: block;
    }
}
</style>
